import { deriveSentilinkView } from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";

import { SENTILINK_RISK_STATUS } from "./sentilinkRiskStatus";

export const BLOCKED_BY_ID_FRAUD_KEY = "blockedByIdFraud";

export const BLOCKED_BY_ID_FRAUD_LABEL = "Blocked by Identity Risk Check";

export const REVIEW_ID_FRAUD_MENU_LABEL = "Review Identity Risk Check";

/** Row kebab is built but hidden until product wants Review Identity Risk Check on the list. */
export const SHOW_BLOCKED_QUEUE_ROW_MENU = false;

/** location.state flag so deal summary can open the Identity Risk Check modal. */
export const OPEN_ID_FRAUD_REVIEW_STATE_KEY = "openIdFraudReview";

export const idFraudReviewLocationState = {
  [OPEN_ID_FRAUD_REVIEW_STATE_KEY]: true as const,
};

export const isOpenIdFraudReviewState = (state?: unknown): boolean =>
  Boolean(
    state &&
      typeof state === "object" &&
      (state as { [OPEN_ID_FRAUD_REVIEW_STATE_KEY]?: boolean })[
        OPEN_ID_FRAUD_REVIEW_STATE_KEY
      ]
  );

/** Same cutoff as BackOffice / `sentilink_record_blocks` (score ≥ 750). */
const SENTILINK_QUEUE_SCORE_THRESHOLD = 750;

const numericScore = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/** Triggered applicant that keeps the deal in the blocked overlay. */
export const isTriggeredSentilinkQueueBlocker = (
  sentilink: unknown
): boolean => {
  const view = deriveSentilinkView(sentilink);
  if (!view?.triggered || view.isManuallyVerified) {
    return false;
  }
  if (view.isBlocked) {
    return true;
  }
  if (!sentilink || typeof sentilink !== "object") {
    return false;
  }
  const row = sentilink as Record<string, unknown>;
  const score = numericScore(row.score);
  const syntheticScore = numericScore(row.syntheticScore);
  return (
    (score != null && score >= SENTILINK_QUEUE_SCORE_THRESHOLD) ||
    (syntheticScore != null && syntheticScore >= SENTILINK_QUEUE_SCORE_THRESHOLD)
  );
};

export const isAwaitingIdFraudResubmission = (sentilink: unknown): boolean =>
  deriveSentilinkView(sentilink)?.notTriggered === "AWAITING";

export const firstSentilinkBlockedApplicantId = (
  applicants: Array<{ id?: string | number | null; sentilink?: unknown }>
): string | null => {
  const list = applicants || [];
  const blocking = list.find((applicant) =>
    isTriggeredSentilinkQueueBlocker(applicant.sentilink)
  );
  if (blocking?.id != null) {
    return String(blocking.id);
  }
  const awaiting = list.find((applicant) =>
    isAwaitingIdFraudResubmission(applicant.sentilink)
  );
  return awaiting?.id != null ? String(awaiting.id) : null;
};

export type IdFraudReviewNavigation =
  | { action: "open"; applicantId: string }
  | { action: "wait" }
  | { action: "consume" };

/** True once SentiLink can be read, or the deal will never load (404 / fetch fail). */
export const isDealApplicationsReadyForIdFraudReview = ({
  applications,
  dealNotFound,
  dealFetchFailed,
}: {
  applications: unknown;
  dealNotFound?: boolean;
  dealFetchFailed?: boolean;
}): boolean =>
  Array.isArray(applications) ||
  Boolean(dealNotFound) ||
  Boolean(dealFetchFailed);

/**
 * One-shot Review navigation: open when a queue applicant is known, wait only
 * while deal.applications (the sentilink merge source) is still missing, and
 * consume on a hydrated miss so location.state does not stick.
 */
export const resolveIdFraudReviewNavigation = ({
  applicants,
  dealApplicationsReady,
}: {
  applicants: Array<{ id?: string | number | null; sentilink?: unknown }>;
  dealApplicationsReady: boolean;
}): IdFraudReviewNavigation => {
  const applicantId = firstSentilinkBlockedApplicantId(applicants);
  if (applicantId) {
    return { action: "open", applicantId };
  }
  if (!dealApplicationsReady) {
    return { action: "wait" };
  }
  return { action: "consume" };
};

export const SENTILINK_BLOCKED_EMPTY_MESSAGE =
  "No applications are blocked by the Identity Risk Check.";

export const NO_PERMISSION_ROW_COPY = "You don't have permission to act on this check.";

export const RE_RUNNING_ROW_COPY = "VERO is running the Identity Risk Check again. Nothing to do until it finishes.";

export const stuckDaysFrom = (iso?: string | null): number | null => {
  if (!iso) {
    return null;
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return null;
  }
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
};

export const stuckDaysLabel = (days: number): string =>
  days === 1 ? "Stuck 1 day" : `Stuck ${days} days`;

/** Overlay-only: untriggered reset-pending applicants show Checking, not a blank name. */
export const sentilinkChipTierForApplicant = ({
  riskTier,
  checkIsRunning,
}: {
  riskTier?: string | null;
  checkIsRunning?: boolean;
}): string | null | undefined => {
  if (
    checkIsRunning &&
    (!riskTier || riskTier === SENTILINK_RISK_STATUS.NOT_STARTED)
  ) {
    return SENTILINK_RISK_STATUS.CHECKING;
  }
  return riskTier;
};

export type BlockedByIdFraudRowState =
  | "awaiting_decision"
  | "re_running"
  | "no_permission";

export const getBlockedByIdFraudRowState = ({
  canResetIdFraudRisk,
  awaitingResubmission,
  isInternalUser = false,
}: {
  canResetIdFraudRisk: boolean;
  awaitingResubmission?: boolean;
  isInternalUser?: boolean;
}): BlockedByIdFraudRowState => {
  // PRODUCT-2831: Vero staff lose reset but keep the normal row, not the no-permission copy.
  if (!canResetIdFraudRisk && !isInternalUser) {
    return "no_permission";
  }
  if (awaitingResubmission) {
    return "re_running";
  }
  return "awaiting_decision";
};

/** Overlay is not a deal_category; search-service ES has no SentiLink. */
export const shouldUseSearchServiceDealsList = ({
  enableSearchService,
  showSearchServiceResults,
  blockedByIdFraudFilterActive,
}: {
  enableSearchService?: boolean;
  showSearchServiceResults?: boolean;
  blockedByIdFraudFilterActive?: boolean;
}) =>
  Boolean(
    enableSearchService &&
      showSearchServiceResults &&
      !blockedByIdFraudFilterActive
  );

export const shouldUseDjangoDealsList = ({
  showSearchServiceResults,
  blockedByIdFraudFilterActive,
}: {
  showSearchServiceResults?: boolean;
  blockedByIdFraudFilterActive?: boolean;
}) => !showSearchServiceResults || Boolean(blockedByIdFraudFilterActive);
